import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import {
    ReviewDestination,
    Tenant,
    TenantSeoConfigInput,
    TenantsQuery,
    TenantType,
} from "src/graphql/generated/graphql";
import {
    attachTenantLogoMutation,
    updateTenantNameMutation,
    updateTenantSEOMutation,
} from "src/graphql/operations/tenants/mutations";
import { tenantQuery, tenantReviewDestinationsQuery, tenantsQuery } from "src/graphql/operations/tenants/query";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";

interface TenantsOptions {
    tenant?: string[];
    type?: TenantType[];
    locale?: string[];
}

export const useTenants = (options: TenantsOptions = {}) => {
    const [tenants, setTenants] = useState<TenantsQuery["tenants"]["items"]>([]);
    const [tenantsTotal, setTenantsTotal] = useState(0);
    const [isLoadingTenants, setIsLoadingTenants] = useState(false);

    const fetchTenants = useCallback(async (options: TenantsOptions, nextToken?: string) => {
        setIsLoadingTenants(true);

        let hasNext = false;
        const { tenant, type, locale } = options || {};
        try {
            const response = await graphqlFetch(tenantsQuery, { nextToken, tenant, type, locale });

            setTenants((tenants) => [...tenants, ...(response.tenants?.items || [])]);
            setTenantsTotal((tenantsTotal) => tenantsTotal + (response.tenants?.items?.length || 0));

            if (response.tenants?.nextToken) {
                hasNext = true;
                fetchTenants(options, response.tenants.nextToken);
            }
        } catch (err) {
            handleError(err, {
                silent: true,
                context: { nextToken, tenant: tenant?.join(","), type: type?.join(","), locale: locale?.join(",") },
            });
        } finally {
            if (!hasNext) {
                setIsLoadingTenants(false);
            }
        }
    }, []);

    useEffect(() => {
        setTenants([]);
        setTenantsTotal(0);
        fetchTenants(options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(options)]);

    return {
        tenants,
        tenantsTotal,
        isLoadingTenants,
        reloadTenants(reloadOptions: TenantsOptions = options) {
            setTenants([]);
            setTenantsTotal(0);
            fetchTenants(reloadOptions);
        },
    };
};

export const useTenant = (tenantId: string) => {
    const [tenant, setTenant] = useState<Tenant>();
    const [isLoadingTenant, setIsLoadingTenant] = useState(false);

    const fetchTenant = useCallback(async (tenantId: string) => {
        setIsLoadingTenant(true);

        try {
            const response = await graphqlFetch(tenantQuery, { tenantId });

            setTenant(response.tenant);
            return response.tenant;
        } catch (err) {
            handleError(err, {
                message: { key: "tenant_load_failed", namespace: "common" },
                context: { tenantId },
            });
        } finally {
            setIsLoadingTenant(false);
        }
    }, []);

    useEffect(() => {
        if (!tenantId) {
            return;
        }

        fetchTenant(tenantId);
    }, [fetchTenant, tenantId]);

    return {
        tenant,
        isLoadingTenant,
        fetchTenant,
    };
};

export const useReviewDestinations = (tenantId: string) => {
    const [reviewDestinations, setReviewDestinations] = useState<ReviewDestination[]>([]);
    const [isLoadingReviewDestinations, setIsLoadingReviewDestinations] = useState(false);

    const fetchReviewDestinations = useCallback(async (tenantId: string) => {
        setIsLoadingReviewDestinations(true);

        try {
            const response = await graphqlFetch(tenantReviewDestinationsQuery, { tenantId });

            setReviewDestinations(response.tenant?.config?.reviewDestinations || []);
        } catch (err) {
            handleError(err, {
                message: { key: "tenant_load_failed", namespace: "common" },
                context: { tenantId },
            });
        } finally {
            setIsLoadingReviewDestinations(false);
        }
    }, []);

    useEffect(() => {
        if (!tenantId) {
            return;
        }

        fetchReviewDestinations(tenantId);
    }, [fetchReviewDestinations, tenantId]);

    return {
        reviewDestinations,
        isLoadingReviewDestinations,
        reloadReviewDestinations: useCallback(() => {
            return fetchReviewDestinations(tenantId);
        }, [fetchReviewDestinations, tenantId]),
    };
};

export const useUpdateTenantName = (tenantId: string) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const [isUpdatingTenantName, setIsUpdatingTenantName] = useState(false);

    const updateTenantName = useCallback(
        async (name: string) => {
            setIsUpdatingTenantName(true);
            showNotification({
                color: "blue",
                message: t("tenant_saving"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(updateTenantNameMutation, {
                    clientId: client?.id,
                    tenantId,
                    name,
                });

                return response.updateTenantName;
            } catch (err) {
                handleError(err, {
                    message: { key: "tenant_saving_failed", namespace: "common" },
                    context: { tenantId, name },
                });
            } finally {
                setIsUpdatingTenantName(false);
            }
        },
        [t, client?.id, tenantId]
    );

    return {
        isUpdatingTenantName,
        updateTenantName,
    };
};

export const useAttachTenantLogo = (tenantId: string) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const [isAttachingTenantLogo, setIsAttachingTenantLogo] = useState(false);

    const attachTenantLogo = useCallback(
        async (imageId: string) => {
            setIsAttachingTenantLogo(true);

            try {
                await graphqlFetch(attachTenantLogoMutation, {
                    clientId: client?.id,
                    tenantId,
                    imageId,
                });

                showNotification({
                    title: t("success"),
                    message: t("success_uploading_image"),
                    color: "green",
                    icon: null,
                });
            } catch (error) {
                handleError(error, {
                    message: { key: "error_uploading_image", namespace: "common" },
                    context: { tenantId, imageId },
                });
            } finally {
                setIsAttachingTenantLogo(false);
            }
        },
        [t, client?.id, tenantId]
    );

    return {
        isAttachingTenantLogo,
        attachTenantLogo,
    };
};

export const useUpdateTenantSEO = (tenantId: string) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const [isUpdatingTenantSEO, setIsUpdatingTenantSEO] = useState(false);

    const updateTenantSEO = useCallback(
        async (seo: TenantSeoConfigInput) => {
            setIsUpdatingTenantSEO(true);
            showNotification({
                color: "blue",
                message: t("tenant_seo_saving"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(updateTenantSEOMutation, {
                    clientId: client?.id,
                    tenantId,
                    seo,
                });

                return response.updateTenantSEO;
            } catch (err) {
                handleError(err, {
                    message: { key: "tenant_seo_saving_failed", namespace: "common" },
                    context: { tenantId, ...seo },
                });
            } finally {
                setIsUpdatingTenantSEO(false);
            }
        },
        [t, client?.id, tenantId]
    );

    return {
        isUpdatingTenantSEO,
        updateTenantSEO,
    };
};
